import { cn } from "@/lib/utils"
import { FileText, Upload } from "lucide-react"
import styles from "./styles.module.scss"
import { useRef, useState, type ChangeEvent, type DragEvent } from "react"
import { useCreateNoteImages, useCreateNoteUploadFile } from "@/features"
import {
  ALLOWED_EXTENSIONS,
  ALLOWED_IMAGE_EXTENSIONS,
} from "@/shared/constants"
import { isSupportedImageFile } from "@/shared/utils"

const documentMimeTypes = [
  "application/pdf",
  "text/plain",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/rtf",
  "application/epub+zip",
  "text/html",
  "text/x-web-markdown",
  "text/rtf",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.oasis.opendocument.text",
  "text/csv",
  "application/csv",
]

const acceptAttribute = [
  "image/*",
  ...ALLOWED_IMAGE_EXTENSIONS.map(ext => `.${ext}`),
  ...documentMimeTypes,
  ...ALLOWED_EXTENSIONS.map(ext => `.${ext}`),
].join(",")

interface Props {
  wrapperClassName?: string
}

export const DragAndDropCreateNote = ({ wrapperClassName }: Props) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const { handleProcessImages } = useCreateNoteImages()
  const { handleProcessFile } = useCreateNoteUploadFile()
  const [isDragOver, setIsDragOver] = useState(false)

  const handleClick = () => {
    inputRef.current?.click()
  }

  const handleProcessFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return
    const fileArray = Array.from(files)
    const isImages = fileArray.every(isSupportedImageFile)
    if (isImages) {
      handleProcessImages(files)
      return
    }
    handleProcessFile(fileArray[0])
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    handleProcessFiles(e.target.files)
  }

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(true)
  }

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
  }

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
    handleProcessFiles(e.dataTransfer.files)
  }

  return (
    <div
      className={cn(
        styles.wrapper,
        isDragOver && styles.dragOver,
        wrapperClassName
      )}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <input
        type="file"
        className="hidden"
        multiple
        accept={acceptAttribute}
        ref={inputRef}
        onChange={handleChange}
      />

      <div className={styles.inner} onClick={handleClick}>
        {isDragOver ? (
          <Upload className="text-primary size-6" />
        ) : (
          <FileText className="text-primary size-6" />
        )}
        <span className={styles.text}>
          {isDragOver ? "Drop it" : "Drag & Drop files here"}
        </span>
      </div>
    </div>
  )
}
